import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, LinearProgress, Paper, Popper, SvgIcon, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import SellerMessageBubble from "../components/SellerMessageBubble";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";

function UploadIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 16V5m0 0-4 4m4-4 4 4M5 15v4h14v-4" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function PdfIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 3h7l4 4v14H7V3Zm7 0v5h5M9.5 13h5M9.5 16h5" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function CheckIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="m6 12 4 4 8-9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function TrashIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 8h10m-8 0 .7 11h4.6L15 8m-5-3h4l1 3H9l1-3Z" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function CloseIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="m7 7 10 10M17 7 7 17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </SvgIcon>
  );
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
function formatUploadedAt(value) {
  return new Intl.DateTimeFormat("en", { day: "2-digit", month: "short", year: "numeric" }).format(value);
}
function useDocumentLayoutMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    const contentWidth = Math.round(d3.scaleLinear().domain([1024, 1920]).range([720, 1000]).clamp(true)(width));
    const previewWidth = Math.round(d3.scaleLinear().domain([760, 1920]).range([200, 252]).clamp(true)(width));
    return {
      ...shared,
      contentWidth,
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([24, 38]).clamp(true)(height)),
      uploadHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([260, 350]).clamp(true)(height)),
      uploadIconSize: Math.round(d3.scaleLinear().domain([760, 1920]).range([46, 56]).clamp(true)(width)),
      uploadTitleSize: Math.round(d3.scaleLinear().domain([760, 1920]).range([12, 15]).clamp(true)(width)),
      historyRowHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([58, 68]).clamp(true)(height)),
      previewWidth,
      previewHeight: Math.round(previewWidth * 1.414),
      previewOffset: Math.round(d3.scaleLinear().domain([760, 1920]).range([10, 16]).clamp(true)(width)),
    };
  }, [shared]);
}

function UploadPanel({ metrics, uploadItems, onSelectFiles, onRemove }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const checkingCount = uploadItems.filter((item) => item.status === "CHECKING").length;
  const readyCount = uploadItems.filter((item) => item.status === "READY").length;
  const hasInvalid = uploadItems.some((item) => item.status === "INVALID");
  const chooseFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    onSelectFiles(files);
  };
  const openPicker = () => inputRef.current?.click();
  const headline = checkingCount > 0
    ? `Checking ${checkingCount} PDF${checkingCount === 1 ? "" : "s"}`
    : readyCount > 0
      ? `${readyCount} PDF${readyCount === 1 ? " is" : "s are"} ready`
      : "Choose PDFs or drag and drop them here";
  return (
    <Paper elevation={0} sx={{ mt: `${metrics.cardGap + 8}px`, overflow: "hidden", border: `1px dashed ${hasInvalid ? alpha(ARAGON_COLORS.burgundy, 0.46) : dragActive ? ARAGON_COLORS.burgundy : alpha(ARAGON_COLORS.navy, 0.18)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: alpha(ARAGON_COLORS.ivory, 0.30), transition: "border-color 160ms ease, background-color 160ms ease" }}>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        hidden
        onChange={(event) => {
          chooseFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <Box
        onDragEnter={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          event.preventDefault();
          if (event.currentTarget === event.target) setDragActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          chooseFiles(event.dataTransfer.files);
        }}
        sx={{ minHeight: `${metrics.uploadHeight}px`, px: `${metrics.cardPadding}px`, display: "grid", placeItems: "center", textAlign: "center", bgcolor: dragActive ? alpha(ARAGON_COLORS.burgundy, 0.035) : alpha(ARAGON_COLORS.navy, 0.012), transition: "background-color 160ms ease" }}
      >
        <Box sx={{ maxWidth: 520 }}>
          <Box sx={{ mx: "auto", width: `${metrics.uploadIconSize}px`, height: `${metrics.uploadIconSize}px`, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: alpha(ARAGON_COLORS.navy, 0.055), color: ARAGON_COLORS.navy }}>
            <UploadIcon sx={{ fontSize: `${Math.round(metrics.uploadIconSize * 0.46)}px` }} />
          </Box>
          <Typography sx={{ mt: "14px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.uploadTitleSize}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>
            {headline}
          </Typography>
          <Typography sx={{ mt: "5px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, lineHeight: 1.5, color: ARAGON_COLORS.steel }}>
            PDF only · multiple files allowed
          </Typography>
          <Button type="button" variant="outlined" onClick={openPicker} sx={{ mt: "14px", minWidth: 118, minHeight: 34, px: "18px", borderColor: alpha(ARAGON_COLORS.navy, 0.24), color: ARAGON_COLORS.navy, bgcolor: alpha(ARAGON_COLORS.ivory, 0.46), borderRadius: `${Math.max(7, metrics.cardRadius - 2)}px`, fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, fontWeight: 700, "&:hover": { borderColor: ARAGON_COLORS.burgundy, bgcolor: alpha(ARAGON_COLORS.burgundy, 0.025) } }}>
            Browse PDFs
          </Button>
        </Box>
      </Box>
      {uploadItems.length > 0 && (
        <Box sx={{ maxHeight: `${Math.max(150, metrics.historyRowHeight * 3)}px`, overflowY: "auto", borderTop: `1px solid ${alpha(ARAGON_COLORS.navy, 0.09)}` }}>
          {uploadItems.map((item, index) => {
            const invalid = item.status === "INVALID";
            const checking = item.status === "CHECKING";
            const ready = item.status === "READY";
            return (
              <Box key={item.id} sx={{ px: `${metrics.cardPadding}px`, py: `${Math.max(10, metrics.cardPadding - 10)}px`, borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}` }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", gap: "12px", alignItems: "center" }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: `${Math.max(6, metrics.cardRadius - 3)}px`, bgcolor: invalid ? alpha(ARAGON_COLORS.burgundy, 0.075) : alpha(ARAGON_COLORS.navy, 0.055), display: "grid", placeItems: "center", color: invalid ? ARAGON_COLORS.burgundy : ARAGON_COLORS.navy }}>
                    <PdfIcon sx={{ fontSize: "21px" }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{item.file.name}</Typography>
                    <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: invalid ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel }}>
                      {formatBytes(item.file.size || 0)} · {invalid ? "Rejected" : checking ? "Checking PDF" : "Ready"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {ready && (
                      <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: alpha(ARAGON_COLORS.navy, 0.06), display: "grid", placeItems: "center", color: ARAGON_COLORS.navy }}>
                        <CheckIcon sx={{ fontSize: "15px" }} />
                      </Box>
                    )}
                    {invalid && <CloseIcon sx={{ fontSize: "21px", color: ARAGON_COLORS.burgundy }} />}
                    {checking && <Typography sx={{ minWidth: 36, textAlign: "right", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{Math.round(item.progress || 0)}%</Typography>}
                    <IconButton type="button" onClick={() => onRemove(item.id)} aria-label={`Remove ${item.file.name}`} size="small" sx={{ color: ARAGON_COLORS.steel, "&:hover": { color: ARAGON_COLORS.burgundy, bgcolor: alpha(ARAGON_COLORS.burgundy, 0.055) } }}>
                      <TrashIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                  </Box>
                </Box>
                <LinearProgress variant="determinate" value={invalid ? 100 : item.progress || 0} sx={{ mt: "10px", height: 4, borderRadius: 999, bgcolor: alpha(ARAGON_COLORS.navy, 0.06), "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: invalid ? ARAGON_COLORS.burgundy : ARAGON_COLORS.navy } }} />
                {invalid && <Typography sx={{ mt: "7px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, lineHeight: 1.45, color: ARAGON_COLORS.burgundy }}>{item.error}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

function PreviewWindow({ preview, metrics }) {
  const [motion, setMotion] = useState({ opacity: 0, scale: 0.965, y: 7 });
  const previewDocument = preview?.document || null;
  useEffect(() => {
    if (!previewDocument) return undefined;
    setMotion({ opacity: 0, scale: 0.965, y: 7 });
    const timer = d3.timer((elapsed) => {
      const progress = Math.min(1, elapsed / 170);
      const eased = d3.easeCubicOut(progress);
      setMotion({ opacity: eased, scale: 0.965 + 0.035 * eased, y: 7 * (1 - eased) });
      if (progress >= 1) timer.stop();
    });
    return () => timer.stop();
  }, [previewDocument]);
  if (!previewDocument || !preview.anchorEl) return null;
  return (
    <Popper open anchorEl={preview.anchorEl} placement="right-start" modifiers={[{ name: "offset", options: { offset: [0, metrics.previewOffset] } }, { name: "flip", options: { fallbackPlacements: ["left-start", "right", "left"] } }, { name: "preventOverflow", options: { padding: 12 } }]} sx={{ zIndex: 1500, pointerEvents: "none" }}>
      <Paper elevation={0} sx={{ width: `${metrics.previewWidth}px`, overflow: "hidden", border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.15)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.ivory, boxShadow: `0 18px 50px ${alpha(ARAGON_COLORS.navy, 0.18)}`, opacity: motion.opacity, transform: `translateY(${motion.y}px) scale(${motion.scale})`, transformOrigin: "top left" }}>
        <Box sx={{ px: "11px", height: 34, display: "flex", alignItems: "center", gap: "7px", borderBottom: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}>
          <PdfIcon sx={{ fontSize: "15px", color: ARAGON_COLORS.burgundy }} />
          <Typography noWrap sx={{ minWidth: 0, flex: 1, fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, fontWeight: 600, color: ARAGON_COLORS.navy }}>{previewDocument.name}</Typography>
          <Typography sx={{ flex: "0 0 auto", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(7, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.08em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>Page 1</Typography>
        </Box>
        <Box sx={{ width: "100%", height: `${metrics.previewHeight}px`, bgcolor: alpha(ARAGON_COLORS.navy, 0.035), overflow: "hidden" }}>
          <Box component="iframe" title={`First page preview of ${previewDocument.name}`} src={`${previewDocument.previewUrl}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`} sx={{ width: "100%", height: "100%", border: 0, display: "block", pointerEvents: "none" }} />
        </Box>
      </Paper>
    </Popper>
  );
}

function HistorySection({ metrics, documents, preview, onPreview, onClosePreview }) {
  const desktop = !metrics.compact;
  return (
    <Box sx={{ mt: `${metrics.cardGap + 24}px` }}>
      <Typography component="h2" sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, lineHeight: 1.08, color: ARAGON_COLORS.navy }}>History of upload</Typography>
      <Typography sx={{ mt: "5px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, lineHeight: 1.5, color: ARAGON_COLORS.steel }}>Previously selected PDFs appear here for quick reference and first-page preview.</Typography>
      {documents.length === 0 ? (
        <Paper elevation={0} sx={{ mt: `${metrics.cardGap}px`, minHeight: `${Math.max(120, metrics.historyRowHeight * 2)}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.ivory, display: "grid", placeItems: "center", textAlign: "center", px: `${metrics.cardPadding}px` }}>
          <Box>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(11, metrics.bodySize)}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>No documents yet</Typography>
            <Typography sx={{ mt: "5px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>PDFs uploaded here will appear in this history.</Typography>
          </Box>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ mt: `${metrics.cardGap}px`, overflow: "hidden", border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.ivory }}>
          {desktop && (
            <Box sx={{ minHeight: 38, px: `${metrics.cardPadding}px`, display: "grid", gridTemplateColumns: "minmax(0,1fr) 128px 82px 86px", gap: "14px", alignItems: "center", borderBottom: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}`, bgcolor: alpha(ARAGON_COLORS.navy, 0.018) }}>
              {["File", "Uploaded", "Size", "Status"].map((label) => <Typography key={label} sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>{label}</Typography>)}
            </Box>
          )}
          {documents.map((document, index) => {
            const active = preview?.document?.id === document.id;
            return (
              <Box
                key={document.id}
                role="button"
                tabIndex={0}
                aria-label={`Preview ${document.name}`}
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") onPreview(document, event.currentTarget);
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType === "mouse") onClosePreview();
                }}
                onClick={(event) => onPreview(document, event.currentTarget, active)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onPreview(document, event.currentTarget, active);
                  }
                }}
                sx={{ minHeight: `${metrics.historyRowHeight}px`, px: `${metrics.cardPadding}px`, display: "grid", gridTemplateColumns: desktop ? "minmax(0,1fr) 128px 82px 86px" : "1fr", gap: desktop ? "14px" : "6px", alignItems: "center", borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}`, bgcolor: active ? alpha(ARAGON_COLORS.navy, 0.045) : "transparent", cursor: "pointer", transition: "background-color 140ms ease", "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.035) }, "&:focus-visible": { outline: `1px solid ${alpha(ARAGON_COLORS.navy, 0.30)}`, outlineOffset: "-2px" } }}
              >
                <Box sx={{ minWidth: 0, display: "grid", gridTemplateColumns: "30px minmax(0,1fr)", gap: "10px", alignItems: "center" }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: `${Math.max(6, metrics.cardRadius - 4)}px`, display: "grid", placeItems: "center", bgcolor: alpha(ARAGON_COLORS.burgundy, 0.055), color: ARAGON_COLORS.burgundy }}><PdfIcon sx={{ fontSize: "17px" }} /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 600, color: ARAGON_COLORS.navy }}>{document.name}</Typography>
                    {!desktop && <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel }}>{formatUploadedAt(document.uploadedAt)} · {formatBytes(document.size)} · {document.status}</Typography>}
                  </Box>
                </Box>
                {desktop && <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>{formatUploadedAt(document.uploadedAt)}</Typography>}
                {desktop && <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>{formatBytes(document.size)}</Typography>}
                {desktop && <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{document.status}</Typography>}
              </Box>
            );
          })}
        </Paper>
      )}
      <PreviewWindow preview={preview} metrics={metrics} />
    </Box>
  );
}

function SellerDocumentContent(metrics) {
  const [uploadItems, setUploadItems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [preview, setPreview] = useState(null);
  const objectUrlsRef = useRef(new Set());
  const hasChecking = uploadItems.some((item) => item.status === "CHECKING");
  useEffect(() => {
    if (!hasChecking) return undefined;
    const duration = 900;
    const timer = d3.timer(() => {
      const now = performance.now();
      setUploadItems((current) => current.map((item) => {
        if (item.status !== "CHECKING") return item;
        const progress = Math.min(100, Math.max(4, ((now - item.startedAt) / duration) * 100));
        if (progress >= 100) return { ...item, status: "READY", progress: 100, error: "" };
        return { ...item, progress };
      }));
    });
    return () => timer.stop();
  }, [hasChecking]);
  useEffect(() => {
    const readyItems = uploadItems.filter((item) => item.status === "READY" && !item.committed);
    if (readyItems.length === 0) return;
    setDocuments((current) => {
      const existingIds = new Set(current.map((item) => item.id));
      const additions = [];
      readyItems.forEach((item) => {
        if (existingIds.has(item.id)) return;
        const previewUrl = URL.createObjectURL(item.file);
        objectUrlsRef.current.add(previewUrl);
        additions.push({ id: item.id, name: item.file.name, size: item.file.size, uploadedAt: new Date(), status: "Ready", previewUrl });
        existingIds.add(item.id);
      });
      return additions.length > 0 ? [...additions.reverse(), ...current] : current;
    });
    const readyIds = new Set(readyItems.map((item) => item.id));
    setUploadItems((current) => current.map((item) => readyIds.has(item.id) ? { ...item, committed: true } : item));
  }, [uploadItems]);
  useEffect(() => () => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current.clear();
  }, []);
  const selectFiles = (files) => {
    const now = performance.now();
    const nextItems = files.map((file, index) => {
      const id = `${file.name}:${file.size}:${file.lastModified}`;
      const looksLikePdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!looksLikePdf) return { id: `${id}:${index}`, file, status: "INVALID", progress: 0, error: "This file was rejected. Aragon accepts PDF documents only.", committed: false };
      if (file.size <= 0) return { id: `${id}:${index}`, file, status: "INVALID", progress: 0, error: "This PDF is empty and cannot be uploaded.", committed: false };
      return { id, file, status: "CHECKING", progress: 4, error: "", committed: false, startedAt: now };
    });
    setUploadItems((current) => {
      const existingIds = new Set(current.map((item) => item.id));
      const additions = [];
      nextItems.forEach((item) => {
        if (existingIds.has(item.id)) return;
        additions.push(item);
        existingIds.add(item.id);
      });
      return [...current, ...additions];
    });
  };
  const removeUploadItem = (id) => setUploadItems((current) => current.filter((item) => item.id !== id));
  const showPreview = (document, anchorEl, toggle = false) => {
    if (toggle && preview?.document?.id === document.id) {
      setPreview(null);
      return;
    }
    setPreview({ document, anchorEl });
  };
  return (
    <Box>
      <Typography component="h1" sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>Document</Typography>
      <Typography sx={{ mt: "6px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, lineHeight: 1.5, color: ARAGON_COLORS.steel }}>Upload and manage your company documents.</Typography>
      <UploadPanel metrics={metrics} uploadItems={uploadItems} onSelectFiles={selectFiles} onRemove={removeUploadItem} />
      <HistorySection metrics={metrics} documents={documents} preview={preview} onPreview={showPreview} onClosePreview={() => setPreview(null)} />
    </Box>
  );
}

export default function SellerDocument() {
  const metrics = useDocumentLayoutMetrics();
  return (
    <LayoutMapping metrics={metrics} sidebar={<WorkspaceSidebar metrics={metrics} />} floatingUtility={<SellerMessageBubble metrics={metrics} />}>
      <SellerDocumentContent {...metrics} />
    </LayoutMapping>
  );
}
